import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../components/ui/sonner';
import { Search, FileText, CheckCircle, XCircle, Shield, Users, FileUp, Trash2, Filter } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const navigate = useNavigate();
  const { t, isRtl } = useLanguage();

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    filterLogs();
  }, [searchTerm, statusFilter, actionFilter, logs]);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/audit-logs?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(response.data);
      setFilteredLogs(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t.accessDenied);
        navigate('/dashboard');
      } else if (error.response?.status === 401) {
        localStorage.removeItem('token');
        toast.error(t.sessionExpired);
        navigate('/login');
      } else {
        toast.error(t.somethingWentWrong);
      }
    } finally {
      setLoading(false);
    }
  };

  const filterLogs = () => {
    let filtered = logs;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(log =>
        log.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.details.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(log => log.status === statusFilter);
    }

    // Action filter
    if (actionFilter !== 'all') {
      filtered = filtered.filter(log => log.action.includes(actionFilter));
    }

    setFilteredLogs(filtered);
  };

  const getActionIcon = (action) => {
    if (action.includes('login')) return Shield;
    if (action.includes('user')) return Users;
    if (action.includes('permission')) return FileText;
    if (action.includes('document')) return FileUp;
    if (action.includes('deleted')) return Trash2;
    return FileText;
  };

  const getActionColor = (action) => {
    if (action.includes('deleted')) return 'text-red-600 bg-red-50';
    if (action.includes('created')) return 'text-green-600 bg-green-50';
    if (action.includes('login_failed')) return 'text-orange-600 bg-orange-50';
    if (action.includes('login')) return 'text-blue-600 bg-blue-50';
    return 'text-slate-600 bg-slate-50';
  };

  // Translations for audit log specific items
  const texts = {
    en: {
      searchPlaceholder: "Search by user, action, or details...",
      allStatus: "All Status",
      success: "Success",
      failure: "Failure",
      allActions: "All Actions",
      logins: "Logins",
      documents: "Documents",
      noLogsFiltered: "No logs found matching your filters",
      noLogsAvailable: "No audit logs available",
      showing: "Showing",
      of: "of",
      auditLogs: "audit logs"
    },
    ar: {
      searchPlaceholder: "البحث حسب المستخدم أو الإجراء أو التفاصيل...",
      allStatus: "جميع الحالات",
      success: "نجاح",
      failure: "فشل",
      allActions: "جميع الإجراءات",
      logins: "تسجيلات الدخول",
      documents: "المستندات",
      noLogsFiltered: "لم يتم العثور على سجلات مطابقة للفلاتر",
      noLogsAvailable: "لا توجد سجلات مراجعة متاحة",
      showing: "عرض",
      of: "من",
      auditLogs: "سجل مراجعة"
    }
  };
  const txt = texts[isRtl ? 'ar' : 'en'];

  return (
    <Layout>
      <div data-testid="audit-logs-page" className="space-y-6">
        <div className={isRtl ? 'text-right' : ''}>
          <h1 className="text-4xl font-bold text-slate-900">{t.auditLogsTitle}</h1>
          <p className="text-slate-600 mt-2">{t.auditLogsSubtitle}</p>
        </div>

        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${isRtl ? 'md:flex-row-reverse' : ''}`}>
          <div className="relative md:col-span-2">
            <Search className={`absolute ${isRtl ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5`} />
            <Input
              data-testid="search-logs-input"
              type="text"
              placeholder={txt.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${isRtl ? 'pr-10 text-right' : 'pl-10'} h-11`}
              dir={isRtl ? 'rtl' : 'ltr'}
            />
          </div>

          <div className={`grid grid-cols-2 gap-2 ${isRtl ? 'direction-rtl' : ''}`}>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className={isRtl ? 'text-right' : ''}>
                <SelectValue placeholder={t.status} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{txt.allStatus}</SelectItem>
                <SelectItem value="success">{txt.success}</SelectItem>
                <SelectItem value="failure">{txt.failure}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className={isRtl ? 'text-right' : ''}>
                <SelectValue placeholder={t.action} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{txt.allActions}</SelectItem>
                <SelectItem value="login">{txt.logins}</SelectItem>
                <SelectItem value="user">{t.users}</SelectItem>
                <SelectItem value="permission">{t.permissions}</SelectItem>
                <SelectItem value="document">{txt.documents}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">{t.loading}</div>
        ) : filteredLogs.length === 0 ? (
          <Card className="p-12 text-center">
            <Filter className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-500">
              {searchTerm || statusFilter !== 'all' || actionFilter !== 'all'
                ? txt.noLogsFiltered
                : txt.noLogsAvailable}
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredLogs.map((log, index) => {
              const Icon = getActionIcon(log.action);
              const colorClass = getActionColor(log.action);
              
              return (
                <Card
                  key={index}
                  data-testid={`audit-log-${index}`}
                  className="p-4 bg-white border border-slate-200 hover:shadow-md transition-shadow"
                >
                  <div className={`flex items-start gap-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
                    <div className={`p-3 rounded-lg ${colorClass}`}>
                      <Icon className="w-5 h-5" />
                    </div>

                    <div className={`flex-1 min-w-0 ${isRtl ? 'text-right' : ''}`}>
                      <div className={`flex items-center gap-3 mb-1 ${isRtl ? 'flex-row-reverse' : ''}`}>
                        <h3 className="font-semibold text-slate-900">
                          {log.action.replace(/_/g, ' ').toUpperCase()}
                        </h3>
                        {log.status === 'success' ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-600" />
                        )}
                      </div>

                      <p className="text-sm text-slate-600 mb-2">{log.details}</p>

                      <div className={`flex flex-wrap items-center gap-4 text-xs text-slate-500 ${isRtl ? 'flex-row-reverse' : ''}`}>
                        <span className={`flex items-center gap-1 ${isRtl ? 'flex-row-reverse' : ''}`}>
                          <Users className="w-3 h-3" />
                          {log.user}
                        </span>
                        <span>
                          {new Date(log.timestamp).toLocaleString(isRtl ? 'ar-SA' : 'en-US', {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          })}
                        </span>
                        {log.ip_address && (
                          <span className="font-mono">{log.ip_address}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {filteredLogs.length > 0 && (
          <div className="text-center text-sm text-slate-500">
            {txt.showing} {filteredLogs.length} {txt.of} {logs.length} {txt.auditLogs}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AuditLogs;
